import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { navigationConfig } from '../../config/navigation';
import useSidebarCollapse from '../../hooks/useSidebarCollapse';
import PoweredByFooter from '../ui/PoweredByFooter';
import { cn } from '../../lib/utils';

function SidebarNavItem({ to, icon: Icon, label, end, isCollapsed }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors',
          isActive
            ? 'bg-white/10 text-white border-l-2 border-sky-400'
            : 'text-white/70 hover:bg-white/10 hover:text-white border-l-2 border-transparent'
        )
      }
      title={isCollapsed ? label : undefined}
    >
      {Icon && <Icon className="text-lg flex-shrink-0" />}
      {!isCollapsed && <span>{label}</span>}
    </NavLink>
  );
}

function SidebarNavMenu({ label, icon: Icon, children, isCollapsed, onExpandSidebar, defaultOpen }) {
  const [isOpen, setIsOpen] = useState(false);

  React.useEffect(() => {
    if (defaultOpen) setIsOpen(true);
  }, [defaultOpen]);

  if (isCollapsed) {
    return (
      <div className="relative group">
        <button
          className="flex items-center justify-center w-full px-4 py-2.5 text-white/70 hover:bg-white/10 hover:text-white rounded-md transition-colors"
          title={label}
          onClick={() => {
            if (onExpandSidebar) onExpandSidebar(label);
          }}
        >
          {Icon && <Icon className="text-lg" />}
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white rounded-md transition-colors"
      >
        {Icon && <Icon className="text-lg flex-shrink-0" />}
        <span>{label}</span>
        {isOpen ? (
          <FaChevronUp className="ml-auto text-xs" />
        ) : (
          <FaChevronDown className="ml-auto text-xs" />
        )}
      </button>
      {isOpen && (
        <div className="ml-4 mt-1 flex flex-col gap-0.5">
          {children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              end
              className={({ isActive }) =>
                cn(
                  'flex items-center px-4 py-2 text-sm rounded-md transition-colors',
                  isActive
                    ? 'text-white bg-white/10 border-l-2 border-sky-400'
                    : 'text-white/60 hover:text-white hover:bg-white/10 border-l-2 border-transparent'
                )
              }
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UnifiedSidebar() {
  const { user } = useAuth();
  const { isCollapsed, toggleCollapse, expand } = useSidebarCollapse();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);

  const handleExpandForMenu = (menuLabel) => {
    expand();
    setExpandedMenu(menuLabel);
  };

  const role = user?.role;
  const navItems = role ? navigationConfig[role] : [];

  if (!navItems || navItems.length === 0) return null;

  const sidebarContent = (
    <>
      {/* Brand */}
      <div className={cn('flex items-center px-4 py-5', isCollapsed ? 'justify-center' : 'gap-3')}>
        <div className="w-8 h-8 bg-white/20 rounded-md flex items-center justify-center">
          <span className="text-white font-bold text-sm">F</span>
        </div>
        {!isCollapsed && <span className="text-white font-bold text-lg tracking-wide">FMPS</span>}
      </div>

      {/* Collapse toggle - desktop only */}
      <div className="hidden md:flex px-2 mb-2">
        <button
          onClick={() => { toggleCollapse(); setExpandedMenu(null); }}
          aria-label="Collapse sidebar"
          className="w-full flex items-center justify-center py-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded-md transition-colors"
        >
          {isCollapsed ? <FaChevronRight className="text-xs" /> : <FaChevronLeft className="text-xs" />}
        </button>
      </div>

      {/* Navigation */}
      <nav role="navigation" className="flex-1 overflow-y-auto px-2 flex flex-col gap-0.5">
        {navItems.map((item) => {
          if (item.children) {
            return (
              <SidebarNavMenu
                key={item.label}
                label={item.label}
                icon={item.icon}
                children={item.children}
                isCollapsed={isCollapsed}
                onExpandSidebar={handleExpandForMenu}
                defaultOpen={expandedMenu === item.label}
              />
            );
          }
          return (
            <SidebarNavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              end={item.end}
              isCollapsed={isCollapsed}
            />
          );
        })}
      </nav>

      {/* Footer */}
      <div className="mt-auto px-2 pb-4">
        {!isCollapsed && (
          <PoweredByFooter className="text-white/40 [&>span]:text-white/60" />
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        className="fixed top-3 left-3 z-50 md:hidden bg-primary-700 text-white p-2 rounded-md shadow-md"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label="Toggle menu"
      >
        {isMobileOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
      </button>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden md:flex flex-col bg-gradient-to-b from-primary-700 to-primary-900 transition-all duration-200',
          isCollapsed ? 'w-[68px]' : 'w-[260px]'
        )}
      >
        {sidebarContent}
      </aside>

      {/* Mobile sidebar */}
      {isMobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileOpen(false)}
          />
          <aside className="fixed top-0 left-0 z-50 h-screen w-[260px] flex flex-col bg-gradient-to-b from-primary-700 to-primary-900 md:hidden">
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
}
